/**
 * Service for handling UK postcodes and address lookups
 * Uses EasyPostcodes.com API via backend endpoint
 */

import { getApiUrl } from '../utils/envValidator';

const BACKEND_API_URL = getApiUrl();

/**
 * Lookup a UK postcode to get address information
 * @param {string} postcode - The UK postcode to lookup
 * @returns {Promise} - Promise resolving to address data
 */
export const lookupPostcode = async (postcode) => {
  try {
    // Format the postcode by removing spaces and converting to uppercase
    const formattedPostcode = postcode.replace(/\s+/g, '').toUpperCase();
    
    const response = await fetch(`${BACKEND_API_URL}/api/postcode/${formattedPostcode}`);
    const data = await response.json();
    
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Invalid postcode');
    }
    
    // Return first address if available, or return success with empty address
    if (data.addresses && data.addresses.length > 0) {
      const firstAddress = data.addresses[0];
      return {
        success: true,
        address: {
          postcode: firstAddress.postcode || formattedPostcode,
          townCity: firstAddress.town_or_city || '',
          county: firstAddress.county || '',
          country: 'United Kingdom'
        }
      };
    }
    
    return {
      success: true,
      address: {
        postcode: formattedPostcode,
        townCity: '',
        county: '',
        country: 'United Kingdom'
      }
    };
  } catch (error) {
    console.error('Postcode lookup error:', error);
    return {
      success: false,
      error: error.message || 'Failed to lookup postcode'
    };
  }
};

/**
 * Validate a UK postcode format
 * @param {string} postcode - The postcode to validate
 * @returns {boolean} - True if the postcode format is valid
 */
export const validateUkPostcode = (postcode) => {
  // UK Postcode regex pattern
  const postcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;
  return postcodeRegex.test(postcode);
};

/**
 * Format a UK postcode with proper spacing
 * @param {string} postcode - The postcode to format
 * @returns {string} - Formatted postcode
 */
export const formatPostcode = (postcode) => {
  if (!postcode) return '';
  
  // Remove all spaces and convert to uppercase
  const cleanPostcode = postcode.replace(/\s+/g, '').toUpperCase();
  
  // If the postcode is too short, return as is
  if (cleanPostcode.length < 5) return cleanPostcode;
  
  // Insert space at the correct position
  return `${cleanPostcode.slice(0, -3)} ${cleanPostcode.slice(-3)}`;
};

/**
 * Get addresses for a postcode using EasyPostcodes API
 * @param {string} postcode - The UK postcode to lookup
 * @param {boolean} includeGeo - Whether to include geolocation data
 * @returns {Promise} - Promise resolving to array of addresses
 */
export const getAddressesForPostcode = async (postcode, includeGeo = false) => {
  try {
    if (!postcode) {
      return {
        success: false,
        error: 'Postcode is required'
      };
    }

    // Format postcode
    const formattedPostcode = formatPostcode(postcode);
    
    // Call backend endpoint (which calls EasyPostcodes API)
    const geoParam = includeGeo ? '&includeGeo=true' : '';
    const response = await fetch(`${BACKEND_API_URL}/api/postcode/${encodeURIComponent(formattedPostcode)}${geoParam}`);
    const data = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Failed to lookup addresses'
      };
    }
    
    return {
      success: true,
      addresses: data.addresses || [],
      count: data.count || 0
    };
  } catch (error) {
    console.error('Address lookup error:', error);
    return {
      success: false,
      error: error.message || 'Failed to lookup addresses'
    };
  }
}; 